import React, { createRef, useState } from 'react';
import T from 'i18n';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import ActionsButton from '../../common/ActionsButton';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { formatLabel } from 'utils/formatLabel';
import { useDispatch, useSelector } from 'react-redux';
import {
  faPlusCircle,
  faTrashAlt,
  faEdit,
  faDownload,
  faExchangeAlt,
  faLink,
  faInfoCircle,
  faClone,
  faPaste,
  faEye,
  faFileUpload,
  faIndent,
  faCut,
  faArrowCircleUp,
  faPencilAlt,
  faMap,
} from '@fortawesome/free-solid-svg-icons';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import { documentModalActions } from '../enums';
import {
  selectPermissions,
  selectFolderPath,
  selectCustomContainers,
  selectContainers,
  selectDownloadedDocuments,
  selectGenerateHyperlinks,
  selectCurrentFolder,
  selectConfidentialityRing,
  selectCurrentUserConfidentialityRing,
  selectUserAiTools,
  selectCurrentCaseId,
  selectIsUploadDrawerOpen,
  selectGlobalSideBarOpen,
  selectGlobalSummaryOpen,
  selectUserSettingPresentMode,
  selectCurrentFile,
} from 'common/selectors';
import history from 'common/history';
import { faChromecast } from '@fortawesome/free-brands-svg-icons';
import FilterChips from './FilterChips';
import useSearchContext from 'features/common/Search/useSearchContext';
import { setAiToolsFilters } from '../redux/setAiToolsFilters';
import { Spinner } from 'features/common';
import { setSidebarOpen } from 'features/common/redux/navbarLayout';
import { WarningOutlined, Refresh } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import {
  clearDocuments,
  fetchDocuments,
  fetchFolder,
  fetchTimeline,
  fetchContainer,
  fetchCaseAnnotations,
} from '../redux/actions';

const buttonActions = ({
  selectedRows,
  isPresenterTools,
  permissions = {},
  downloadedDocuments,
  currentFolder,
  openDialog,
  handleShow,
  showGenerateHyperlinks,
  isAllDocuments,
  getFolderSpecificFileDetails,
  isConfidentialityRing,
  openDialogCR,
  showAiTools,
  sortMode,
  handleSidebar,
  fileCount,
  globalBatchDrawerOpen,
  showSidebar,
  setEditMode,
  editMode,
  openMapDataDrawer,
  inlineEdit,
  isDocFinderPage,
  showPresentModeWithSearch,
  handleAiToolsClicked,
}: any) => {
  const {
    folders: {
      canUploadDocuments = undefined,
      canRemoveDocuments = undefined,
      canDownloadDocuments = undefined,
    } = {},
    documents: {
      canUpdateAnyData = undefined,
      canReplace = undefined,
      canSplit = undefined,
      canGeneratePublicHyperlinks = undefined,
      canSeeFileVersions = undefined,
      canAssignDocsToRings = undefined,
      canCreateAnnotations = undefined,
      canUploadTranscriptNotes = undefined,
    } = {},
    hearingRoom: { canPresent = undefined } = {},
  } = permissions;

  const canDownload = canDownloadDocuments;
  const canUpload = canUploadDocuments && canUploadDocuments();
  const canRemove = canRemoveDocuments && canRemoveDocuments();
  const canUpdateData =
    canUpdateAnyData && selectedRows.every((file: any) => canUpdateAnyData(file));
  const canGeneratePublicHyperlinksFile =
    !isPresenterTools && canGeneratePublicHyperlinks && canGeneratePublicHyperlinks();
  const canShowFileVersions = canSeeFileVersions && canSeeFileVersions(selectedRows[0]);
  const assignDocsToRing = canAssignDocsToRings && canAssignDocsToRings(selectedRows[0]);
  const canReplaceFile = canReplace && canReplace(selectedRows[0]);
  const canSplitFile = canSplit && canSplit(selectedRows[0]);
  const selectedDocumentBeeingDownloaded =
    (selectedRows[0] &&
      downloadedDocuments.find(({ fileId }: any) => fileId === selectedRows[0].id)) ||
    {};

  const batchUploadEnabled = permissions.documents.batchUpload;
  // check if the selectRows Array contains a file that is "agreed"
  const hasAgreed = selectedRows.some((file: any) => file.agreed);

  const changePositionDisabled = hasAgreed || sortMode === false || selectedRows.length < 1;

  return [
    canUpload &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly && {
        title: T.translate('generic.uploadFile'),
        handler: openDialog,
        icon: faPlusCircle,
        disabled: showSidebar,
      },
    isConfidentialityRing &&
      canUpload &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly && {
        title: T.translate('generic.uploadFileInner'),
        handler: openDialogCR,
        icon: faPlusCircle,
      },
    canUpload &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly && { divider: true },
    canDownload && {
      id: 'download',
      title: T.translate('generic.download'),
      handler: () => {
        handleShow(documentModalActions.download);
      },
      icon: faDownload,
      disabled: selectedRows.length !== 1 || !!selectedDocumentBeeingDownloaded.progress,
    },
    batchUploadEnabled && { divider: true },

    batchUploadEnabled &&
      !isAllDocuments &&
      !isPresenterTools &&
      currentFolder &&
      !currentFolder.readOnly &&
      !globalBatchDrawerOpen && {
        title: T.translate('generic.batchUpload'),
        handler: handleSidebar,
        icon: faArrowCircleUp,
        disabled: !batchUploadEnabled,
      },

    batchUploadEnabled && // use same permission as upload
      !isAllDocuments &&
      currentFolder && {
        title: T.translate('generic.mapData'),
        handler: openMapDataDrawer,
        icon: faMap,
        disabled: fileCount < 1,
      },

    (canUpload || canDownload) && { divider: true },

    {
      title: T.translate('generic.copyTo'),
      handler: () => handleShow(documentModalActions.copy),
      icon: faFolderOpen,
      disabled: selectedRows.length < 1 || isDocFinderPage,
    },
    canRemove &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.removeFrom'),
        handler: () => handleShow(documentModalActions.remove),
        icon: faTrashAlt,
        disabled: selectedRows.length < 1,
      },

    (canRemove || canUpload) && canUpdateData && { divider: true },
    inlineEdit && {
      title: editMode ? T.translate('generic.stopEditing') : T.translate('generic.editNames'),
      icon: faPencilAlt,
      handler: () => setEditMode((prev: boolean) => !prev),
    },
    canUpdateData &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        id: 'updateMeta',
        title: (
          <span style={{ display: 'inline-flex', width: '100%' }}>
            {T.translate('generic.updateMeta')}
            <span className="shortcutsText">{T.translate('common.openUpdateModalShrtcut')}</span>
          </span>
        ),
        subTitle: T.translate('generic.updateMeta'),
        handler: () => handleShow(documentModalActions.updateMeta),
        icon: faEdit,
        disabled: selectedRows.length !== 1,
      },
    canUpdateData &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        id: 'changePosition',
        title: `${T.translate('generic.changePosition')}`,
        handler: () => handleShow(documentModalActions.changePosition),
        icon: faIndent,
        disabled: changePositionDisabled,
      },
    canReplaceFile &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.replaceFile'),
        handler: () => handleShow(documentModalActions.replace),
        icon: faExchangeAlt,
        disabled: selectedRows.length !== 1,
      },
    canSplitFile &&
      !isPresenterTools &&
      !currentFolder.readOnly && {
        title: T.translate('generic.splitFile'),
        handler: () => handleShow(documentModalActions.split),
        icon: faCut,
        disabled: selectedRows.length !== 1,
      },
    {
      title: T.translate('generic.view'),
      handler: () => getFolderSpecificFileDetails(selectedRows, 'normal'),
      icon: faEye,
      disabled: selectedRows.length !== 1,
    },
    (canUpdateData || canReplaceFile) &&
      canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && { divider: true },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        header: T.translate('generic.generateHyperlinksFrom'),
        title: T.translate('generic.findAndReplaceHyperlinks'),
        handler: () => handleShow(documentModalActions.findAndReplaceHyperlinks),
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },

    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        title: T.translate('generic.generateHyperlinksPreview'),
        handler: () => handleShow(documentModalActions.previewHyperlinks), // from external id
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        title: T.translate('generic.generateHyperlinksPreviewWithDiscoveryId'),
        handler: () => handleShow(documentModalActions.previewHyperlinksWithDiscoveryID), // from discovery id
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },
    canGeneratePublicHyperlinksFile &&
      showGenerateHyperlinks &&
      !isAllDocuments && {
        header: T.translate('generic.manageHyperlinksHeader'),
        title: T.translate('generic.manageHyperlinks'),
        handler: () => handleShow(documentModalActions.manageHyperlinks),
        icon: faLink,
        disabled: selectedRows.length !== 1,
      },

    canGeneratePublicHyperlinks && showGenerateHyperlinks && !isAllDocuments && { divider: true },
    canPresent &&
      canPresent('public') && {
        id: 'presentToHearingRoom',
        title: (
          <span style={{ display: 'inline-flex', width: '100%' }}>
            {T.translate('generic.present')}
            <span className="shortcutsText">
              {T.translate('common.presentToHearingRoomShortcut')}
            </span>
          </span>
        ),
        subTitle: T.translate('generic.present'),
        handler: () => {
          history.push(
            history.location.pathname.replace(/\/files.*/, ``) +
              `/files/${selectedRows[0].id}?present=public`,
          );
        },
        icon: faChromecast,
        disabled: selectedRows.length !== 1 || selectedRows[0].private,
      },

    canPresent &&
      showPresentModeWithSearch &&
      canPresent('public') && {
        title: T.translate('generic.presentView'),
        handler: () => getFolderSpecificFileDetails(selectedRows, 'share'),
        icon: faEye,
        disabled: selectedRows.length !== 1 || (!canPresent && !canPresent('public')),
      },
    canCreateAnnotations && { divider: true },
    canUploadTranscriptNotes && {
      id: 'transcriptNotes',
      title: T.translate('generic.transcriptNotes'),
      handler: () => handleShow(documentModalActions.previewTranscriptNotes),
      icon: faFileUpload,
      disabled: selectedRows.length !== 1,
    },
    { divider: true },
    canShowFileVersions && {
      title: T.translate('generic.fileVersions'),
      handler: () => handleShow(documentModalActions.fileVersions),
      icon: faClone,
      disabled: selectedRows.length !== 1,
    },
    isConfidentialityRing &&
      assignDocsToRing && {
        title: T.translate('generic.fileVariants'),
        handler: () => handleShow(documentModalActions.fileVariants),
        icon: faPaste,
        disabled: selectedRows.length !== 1,
      },
    {
      title: T.translate('generic.more'),
      handler: () => handleShow(documentModalActions.moreInfo),
      icon: faInfoCircle,
      disabled: selectedRows.length !== 1,
    },
    showAiTools && { divider: true },
    showAiTools && {
      title: T.translate('case.askAI'),
      handler: () => handleAiToolsClicked(selectedRows),
      icon: {
        type: 'muiIcon',
        component: <AutoAwesomeIcon sx={{ ml: '-0.5rem', mt: '-0.2rem' }} />,
      },
      disabled: selectedRows.length === 0 || selectedRows.length > 5,
    },
  ];
};

const breadcrumbMeasuredRef = createRef<any>();

export default ({
  filterCategory,
  selectedRows,
  openDialog,
  handleShow,
  isAllDocuments,
  isPresenterTools,
  category,
  getFolderSpecificFileDetails,
  openDialogCR,
  sortMode,
  handleSidebar,
  isDocFinderPage,
  fileCount,
  showSidebar,
  batchUploading,
  setEditMode,
  editMode,
  openMapDataDrawer,
  mappedRowsWithErrors,
  handleOpenAssignDocuments,
  showAIIcon,
}: any) => {
  const [collapseBreadCrumb, setCollapseBreadCrumb] = useState(true);
  //@ts-ignore
  const path = useSelector((state) => selectFolderPath(state, category));
  const isCustomContainers = useSelector(selectCustomContainers);
  const containers = isCustomContainers && useSelector(selectContainers);
  const permissions = useSelector(selectPermissions);
  const { clearSearch } = useSearchContext();
  const downloadedDocuments = useSelector(selectDownloadedDocuments);
  const showGenerateHyperlinks = useSelector(selectGenerateHyperlinks);
  const currentFolder = useSelector(selectCurrentFolder);
  const currrentUserCR = useSelector(selectCurrentUserConfidentialityRing);
  const globalBatchDrawerOpen = useSelector(selectIsUploadDrawerOpen);
  const showPresentModeWithSearch = useSelector(selectUserSettingPresentMode);
  const isSummaryOpen = useSelector(selectGlobalSummaryOpen);
  const isFileOpen = useSelector(selectCurrentFile);
  const {
    documents: { inlineEdit },
  } = useSelector(selectPermissions);
  const isConfidentialityRing =
    useSelector(selectConfidentialityRing) && currrentUserCR !== 'noRing';
  const showAiTools = useSelector(selectUserAiTools);
  const breadcrumbWidth =
    (breadcrumbMeasuredRef &&
      breadcrumbMeasuredRef.current &&
      breadcrumbMeasuredRef.current.offsetWidth) ||
    0;
  const caseId = useSelector(selectCurrentCaseId);
  const isSidebarOpen = useSelector(selectGlobalSideBarOpen);
  const dispatch = useDispatch();

  const handleAiToolsClicked = (files: any, type?: string) => {
    if (type === 'files') {
      dispatch(
        setAiToolsFilters({ files: files.map((file: any) => ({ id: file.id, name: file.name })) }),
      );
    } else if (type === 'folders') {
      dispatch(setAiToolsFilters({ folder: { id: currentFolder.id, name: currentFolder.name } }));
    }
    history.push(history.location.pathname.replace(/\/case.*/, ``) + `/case/${caseId}/ai-tools`);
    if (!isSidebarOpen) dispatch(setSidebarOpen(true));
  };
  const checkPathForCustomContainers = (val: any) => {
    if (containers) {
      if (val === T.translate('case.trialbooks')) return formatLabel(containers[0]);
      else if (val === T.translate('case.courtBundles')) return formatLabel(containers[1]);
      else if (val === T.translate('case.bundles')) return formatLabel(containers[2]);
      else return val;
    } else return val;
  };

  // Check if current URL contains specific modes where refresh should be hidden
  const shouldHideRefresh = () => {
    const currentPath = window.location.pathname + window.location.search;
    return (
      currentPath.includes('/present-mode') ||
      currentPath.includes('/search-mode') ||
      currentPath.includes('/docFinder') ||
      currentPath.includes('/all-annotations')
    );
  };

  const shouldShowAIIcon = () => {
    const currentPath = window.location.pathname + window.location.search;
    return (
      currentPath.includes('/trialbundles/') ||
      currentPath.includes('/teamBundles/') ||
      currentPath.includes('/privateBundles/')
    );
  };

  const handleRefresh = () => {
    dispatch(clearDocuments());
    if (currentFolder?.type === 'quicklinks') {
      dispatch(fetchDocuments({} as any));
    } else if (currentFolder?.type === 'timelines') {
      dispatch(fetchTimeline({ timelineId: currentFolder.id }));
    } else if (currentFolder?.type === 'tags') {
      dispatch(fetchCaseAnnotations({ tagId: currentFolder.id }));
    } else if (currentFolder?.type === 'all-annotations') {
      dispatch(fetchCaseAnnotations({ isAllAnnotations: true }));
    } else if (currentFolder?.id) {
      dispatch(fetchFolder({ folderId: currentFolder.id }));
      dispatch(fetchDocuments({} as any));
    } else {
      dispatch(fetchDocuments({} as any));
    }
    if (containers) {
      dispatch(fetchContainer({}));
    }
  };

  const handleBreadCrumbCollapse = (collapse: boolean) => {
    if (path.length > 2) {
      setCollapseBreadCrumb(collapse);
    } else {
      setCollapseBreadCrumb(collapse);
    }
  };

  const showBreadCrumb = () => {
    return batchUploading ? (
      <>
        <Breadcrumb ref={breadcrumbMeasuredRef}>
          {path.length > 0 && <Breadcrumb.Item active>{`${path[0]}`}</Breadcrumb.Item>}
          <Breadcrumb.Item active>{` ${T.translate('generic.loading')}`}</Breadcrumb.Item>
        </Breadcrumb>
        <Spinner style={{ height: '1.5rem' }} />
      </>
    ) : (
      <div style={{ cursor: 'pointer', flexDirection: 'row' }}>
        <Breadcrumb ref={breadcrumbMeasuredRef}>
          {collapseBreadCrumb ? (
            <Breadcrumb ref={breadcrumbMeasuredRef} onClick={() => handleBreadCrumbCollapse(false)}>
              <Breadcrumb.Item active={true}>
                {checkPathForCustomContainers(path[0])}
              </Breadcrumb.Item>
              {path.length > 2 && (
                <Breadcrumb.Item href="" active={true}>
                  ...
                </Breadcrumb.Item>
              )}
              {path.length > 1 && <Breadcrumb.Item active>{path[path.length - 1]}</Breadcrumb.Item>}
            </Breadcrumb>
          ) : (
            <>
              <Breadcrumb.Item onClick={() => handleBreadCrumbCollapse(true)} active={true}>
                {checkPathForCustomContainers(path).join(' / ')}
              </Breadcrumb.Item>
            </>
          )}
        </Breadcrumb>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '1rem',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {/* Left section - Breadcrumb and Filter Chips */}
      {
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0, // Allow shrinking
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {showBreadCrumb()}
            {mappedRowsWithErrors.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  marginLeft: '10px',
                }}
              >
                <WarningOutlined sx={{ color: '#ECD474' }} />
                <span style={{ marginLeft: '4px' }}>
                  - Data Mapping Error - File not found in CSV
                </span>
              </div>
            )}
          </div>
          <PerfectScrollbar
            style={{
              maxWidth: '100%',
              flexGrow: 1,
              marginTop: '4px',
            }}
            className="filterChipSection"
            options={{ suppressScrollY: true, useBothWheelAxes: true }}
          >
            <FilterChips filterCategory={filterCategory} clearFilter={clearSearch} />
          </PerfectScrollbar>
        </div>
      }
      {/* Right section - Action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          justifyContent: 'flex-end',
          flexShrink: 0, // Prevent shrinking
        }}
      >
        {showAIIcon && !isFileOpen && !isSummaryOpen && showAiTools && shouldShowAIIcon() && (
          <>
            <Tooltip
              title={T.translate('case.useAIForFilesDisabled')}
              enterDelay={0}
              leaveDelay={0}
              arrow
            >
              <span>
                <IconButton
                  onClick={() => handleAiToolsClicked(selectedRows, 'files')}
                  size="small"
                  disabled={selectedRows?.length <= 1}
                  sx={{
                    color: selectedRows?.length > 1 ? '#000' : '#666',
                    opacity: selectedRows?.length > 1 ? 0.75 : 0.5,
                    marginRight: '0.3rem',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                      color: selectedRows?.length > 1 ? '#000' : '#666',
                      opacity: selectedRows?.length > 1 ? 1 : 0.5,
                    },
                    '&.Mui-disabled': {
                      color: '#666',
                      opacity: 0.5,
                    },
                  }}
                >
                  <span
                    style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}
                  >
                    <AutoAwesomeIcon />
                    <span style={{ marginLeft: '2px', fontSize: '11px' }}>
                      {T.translate('case.askAboutFiles')}
                    </span>
                  </span>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={T.translate('case.selectedFolderAITooltip')}
              enterDelay={0}
              leaveDelay={0}
              arrow
            >
              <IconButton
                onClick={() => handleAiToolsClicked(selectedRows, 'folders')}
                size="small"
                sx={{
                  color: '#4567e5',
                  opacity: 0.75,
                  marginLeft: '0.5rem',
                  marginRight: '0.3rem',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    color: '#4567e5',
                    opacity: 1,
                  },
                }}
              >
                <span
                  style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <AutoAwesomeIcon />
                  <span style={{ marginLeft: '2px', fontSize: '11px' }}>
                    {T.translate('case.askAboutFolder')}
                  </span>
                </span>
              </IconButton>
            </Tooltip>
          </>
        )}
        {!shouldHideRefresh() && (
          <IconButton
            onClick={handleRefresh}
            size="small"
            title="Refresh"
            sx={{
              color: '#6c757d',
              marginRight: '1rem',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0)',
                color: '#5a6268',
              },
            }}
          >
            <Refresh />
          </IconButton>
        )}
        <ActionsButton
          buttonActions={buttonActions({
            selectedRows,
            permissions,
            isPresenterTools,
            downloadedDocuments,
            currentFolder,
            openDialog,
            handleShow,
            showGenerateHyperlinks,
            isAllDocuments,
            getFolderSpecificFileDetails,
            dispatch,
            isConfidentialityRing,
            openDialogCR,
            showAiTools,
            caseId,
            sortMode,
            handleSidebar,
            path,
            fileCount,
            showSidebar,
            setEditMode,
            editMode,
            globalBatchDrawerOpen,
            openMapDataDrawer,
            inlineEdit,
            isDocFinderPage,
            isSidebarOpen,
            handleOpenAssignDocuments,
            showPresentModeWithSearch,
            handleAiToolsClicked,
          })}
        />
      </div>
    </div>
  );
};
